import React, { useState, useEffect } from "react";
import OfferStep from "./OfferStep";
import PrimaryOutlineButton from "../PrimaryOutlineButton";
import { store } from "../../../redux/store";

const OfferFormLayout = ({
  title,
  breadcrumbs,
  content,
  stepIndex,
  handleSaveAndExit,
  onStepClick,
}) => {
  // Use local state instead of useSelector to avoid reducer execution conflicts
  const [offerData, setOfferData] = useState(null);
  const [role, setRole] = useState(null);
  
  // Safely update state from Redux store
  useEffect(() => {
    const updateState = () => {
      try {
        const state = store.getState();
        setOfferData(state?.offerReducer?.createdOffer || null);
        setRole(state?.authReducer?.tokenData?.user?.role || null);
      } catch (error) {
        console.warn('OfferFormLayout store access error:', error.message);
      }
    };
    
    const timeoutId = setTimeout(updateState, 100);
    return () => clearTimeout(timeoutId);
  }, []);
  
  // Subscribe to store changes
  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      try {
        const state = store.getState();
        setOfferData(state?.offerReducer?.createdOffer || null);
        setRole(state?.authReducer?.tokenData?.user?.role || null);
      } catch (error) {
        console.warn('OfferFormLayout store subscription error:', error.message);
      }
    });
    
    return unsubscribe;
  }, []);
  
  // Base steps for regular users
  const baseSteps = [
    { stepNumber: 1, content: "Select Category" },
    { stepNumber: 2, content: "Select Sub Category" },
    { stepNumber: 3, content: "Create Offer" },
    { stepNumber: 4, content: "Customize Your Offer" },
    { stepNumber: 5, content: "Rules and Regulations" },
  ];

  // Admin steps with "Select Merchant" at the beginning
  const adminBaseSteps = [
    { stepNumber: 1, content: "Select Merchant" },
    { stepNumber: 2, content: "Select Category" },
    { stepNumber: 3, content: "Select Sub Category" },
    { stepNumber: 4, content: "Create Offer" },
    { stepNumber: 5, content: "Customize Your Offer" },
    { stepNumber: 6, content: "Rules and Regulations" },
  ];

  // Choose the appropriate base steps based on role
  const currentBaseSteps = role === "admin" ? adminBaseSteps : baseSteps;

  // Add conditional steps based on offer type
  const formSteps = offerData?.offerType === "INSTORE_SWOODLE_OFFER" 
    ? [
        ...currentBaseSteps,
        { stepNumber: currentBaseSteps.length + 1, content: "Locations" },
        { stepNumber: currentBaseSteps.length + 2, content: "Review and Approve" },
      ]
    : [
        ...currentBaseSteps,
        { stepNumber: currentBaseSteps.length + 1, content: "Review and Approve" },
      ];

  return (
    <div className="col-span-12 mainFormDiv flex flex-col">
      {/* Sticky Header and Stepper */}
      <div className="sticky top-0 z-20 bg-white">
        {/* Header/Breadcrumbs with border bottom */}
        <div className="py-4 px-7 border-b" style={{ borderBottom: "1px solid #D9D9D9" }}>
          <div className="flex justify-between items-center gap-3">
            {breadcrumbs ? (
              <div className="flex-1">
                {breadcrumbs}
              </div>
            ) : title ? (
              <p
                className={`sm:text-[18px] md:text-[20px] font-[600] text-[#303030] font-poppins text-center`}
              >
                {title}
              </p>
            ) : null}
            {stepIndex !== 1 && <PrimaryOutlineButton
              label={"Save and Exit"}
              className={
                "w-[40%] sm:w-[30%] md:w-[25%] lg:w-[20%] xl:w-[12%] text-[14px] font-[500] h-[40px] sm:h-[40px] md:h-[45px] rounded-[5px]"
              }
              onClick={handleSaveAndExit}
            />}
          </div>
        </div>
        {/* Stepper (no border) */}
        <div className="px-7 w-[100%] bg-white">
          <div className="flex justify-between sm:flex-col lg:flex-row">
            <div className="flex items-center justify-between w-full mt-4">
              {formSteps?.map((step) => (
                <OfferStep
                  key={step.stepNumber}
                  stepNumber={step?.stepNumber}
                  content={step?.content}
                  stepIndex={stepIndex}
                  isClickable={step.stepNumber < stepIndex}
                  onClick={onStepClick}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="px-7 w-[100%] pb-6">
        {content}
      </div>
    </div>
  );
};

export default OfferFormLayout;